import { Card, Typography, Grid, CircularProgress, Table, TableCell, TableRow, TableHead, TableBody, Chip, Box, Alert, Tooltip, tooltipClasses, IconButton, SpeedDial, SpeedDialAction} from '@mui/material';
import { CheckCircle, Droplet, Sunrise, Sun, Moon, Info, ChartNoAxesCombined, FileStack, FolderPlus, MoveUpRight, MoveDownRight, EllipsisVertical } from 'lucide-react';
//import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useTranslation } from '../../../../language';
import { API_URL } from '../../../../constants';
import CustomDialogSmall from '../../../../components/Dialog/DiaCompSmall';
import BloodMarkerAddForm from '../../../../components/API/BloodMarkerAddForm';
import { GetHistoryTable } from './GetHistoryTable';
import { TextInfoComp } from './TextInfoComp';
import BloodLineChart from './BloodLineChart';
import ReferenceGauge from '../../../../components/Charts/LinearGauge/ReferenceGauge';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const MarkerCardComp = ({ marker, userId }) => {

const { t, language } = useTranslation();
const containerRef = useRef(null);
const [width, setWidth] = useState(550);
const theme = useTheme();
const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

const latestValue = parseFloat(marker.value);
const latestDate = new Date(marker.date).toLocaleDateString();


// Dialog states
  const [openInfo, setOpenInfo] = useState(false);
  const [openChart, setOpenChart] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [open, setOpen] = useState(false);

 // Base card style
  const baseCardStyles = {
    width: { xs: 380, sm: 400, md: 400, lg:500, xl: 540 }, borderRadius: 4, position: 'relative', overflow: 'visible' }

  const cardStyles =
    theme.palette.mode === "light"
      ? {bgcolor: 'grey.C50', borderColor: 'grey.C900',}
      : 
      {bgcolor: 'greydark.C900', borderColor: 'greydark.C400', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', // Safari
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',};

  // Colors
  const statusColors = {
    green: theme.palette.traffic.green,
    orange: theme.palette.traffic.orange,
    red: theme.palette.traffic.red,
    unknown: 'grey.C100'
  };

const lineColor = statusColors[marker.status];

let StatusIcon = null;
let statusText = "";
let statusColor = "";

if (marker.status === "green") {
  StatusIcon = CheckCircle;
  statusText = "Normal";
  statusColor = statusColors.green;
} else if (marker.status === "orange") {
  StatusIcon = latestValue > marker.reference.upper_green ? MoveUpRight : MoveDownRight;
  statusText = latestValue > marker.reference.upper_green ? "Elevated" : "Low";
  statusColor = statusColors.orange;
} else if (marker.status === "red") {
  StatusIcon = latestValue > marker.reference.upper_green ? MoveUpRight : MoveDownRight;
  statusText = latestValue > marker.reference.upper_green ? "High" : "Low";
  statusColor = statusColors.red;
}
// for charts
const history = marker.history || [];
const chartData = history.map((h) => parseFloat(h.value));
const xLabels = history.map((h) => new Date(h.date));
  // Latest record info
  const latestIndex = chartData.length - 1;
  const latestPoint = [{ x: xLabels[latestIndex], y: chartData[latestIndex] },];

return (
<>

 <Card variant="outlined" sx={{ ...baseCardStyles, ...cardStyles }}>
  
  <Grid container sx={{px:2.5, py:1}}>
  
          <Grid size={8} >
          {/* Marker Name, Short Description, */}   
            <Typography variant="subtitle1" fontWeight="400" gutterBottom={false} sx={{lineHeight:1, fontWeight: 500}}>
              {t(`markername_${marker.short}`)} 
            </Typography>
            <Typography variant="subtitle2" fontWeight="400"  sx={{lineHeight:2, mb:1.4}}>
              {t(`markerdescr_${marker.short}`)}
              </Typography>
          </Grid>

          {/* INFO Button */}
          <Grid  size={1} sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            <Tooltip title="Info" placement="top">
                <IconButton size="small" onClick={() => setOpenInfo(true)} sx={{ '&:hover': {transform: 'scale(1.2)'}, }} >
                    <Info size={18} sx={{ width: 28, height: 28, minHeight: 28, boxShadow: "none"}}/>
                </IconButton>   
            </Tooltip>
          </Grid>

           {/* CHART Button */}
          <Grid  size={1} sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            <Tooltip title="Chart" placement="top">
                <IconButton size="small" onClick={() => setOpenChart(true)} sx={{ '&:hover': {transform: 'scale(1.2)'}, }} >
                    <ChartNoAxesCombined size={18} sx={{ width: 28, height: 28, minHeight: 28, boxShadow: "none"}}/>
                </IconButton>   
            </Tooltip>
          </Grid>

          {/* LIST Button */}
          <Grid  size={1} sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            <Tooltip title="List Measurements" placement="top">
                <IconButton size="small" onClick={() => setOpenHistory(true)} sx={{ '&:hover': {transform: 'scale(1.2)'}, }} >
                    <FileStack size={18} sx={{ width: 28, height: 28, minHeight: 28, boxShadow: "none"}}/>
                </IconButton>   
            </Tooltip>
          </Grid>

          {/* ADD Button */}
          <Grid  size={1} sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            <Tooltip title="Add Data" placement="top">
                <IconButton size="small" onClick={() => setOpenAdd(true)} sx={{ '&:hover': {transform: 'scale(1.2)'}, }} >
                    <FolderPlus size={18} sx={{ width: 28, height: 28, minHeight: 28, boxShadow: "none"}}/>
                </IconButton>   
            </Tooltip>
          </Grid>

          {/*  Value Unit, Status Icon, Cycle Info Female */}
              <Grid size={8}  sx={{ display: 'flex', alignItems: 'flex-start'}}>
                 
                  {/* Status Color Bar */}
                <Box sx={{ borderLeft: `8px solid ${lineColor}`}}> 
          
                  {/* Value & Unit */}
                  
                    <Typography variant="number" sx={{pl:1, lineHeight:1.1}}>
                      {latestValue}
                      <Typography component="span" variant="body1">
                        &nbsp;&nbsp;{marker.unit}
                      </Typography>
                    </Typography> 
                  </Box>
                 
          
                  {/* Status Icon & Color */}
                    <Box sx={{pl:1}}>
                        {StatusIcon && (
                      <HtmlTooltip
                          title= {<Typography variant="body2" sx={{ color: statusColor, textTransform: 'uppercase', fontWeight: 400  }}>&nbsp;{statusText}</Typography> }
                          placement="right"
                          disableFocusListener
                          >
                      <IconButton > <StatusIcon size={18} strokeWidth={["orange", "red"].includes(marker.status) ? 5 : 2} color={statusColor}/> </IconButton>
                      </HtmlTooltip>
                        )}  
                    </Box>
            
                </Grid>

                {/* Reference Gauge */}
                <Grid size={4}  sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', }}>
                  <Grid container sx={{width:"95%",}}>
                  <Grid size={12}>
                  <Box sx={{ borderRadius:1, bgcolor:  theme.palette.mode === "light" ? 'grey.C50' : 'none'}}>
                    <ReferenceGauge
                      value={latestValue}
                      reference={marker.reference}
                      arrowColor={ theme.palette.mode === "light" ? theme.palette.greydark.C400 : theme.palette.grey.C050}
                    />
                  </Box>
                  </Grid>
              {/* Reference Range */}
                    <Grid size={12} sx={{mt:0.8, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                    <Typography variant="subtitle2" sx={{fontWeight: 400}}>Ref: {marker.reference.lower_green} - {marker.reference.upper_green} {marker.unit} </Typography>
                  </Grid>  
              </Grid>   
              </Grid>

 

  </Grid>
    </Card>


 {/* Info Dialog - Translation already included - key forwarding*/}
      <CustomDialogSmall
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title="Information"
        content={     

        <TextInfoComp 
          description={`description_${marker.short}`}
          lowtext={`lowtext_${marker.short}`}
          lowrec={`lowrec_${marker.short}`}
          rangetext={`rangetext_${marker.short}`}
          hightext={`hightext_${marker.short}`}
          highrec={`highrec_${marker.short}`} 
          />
        }
      />

     {/* Chart Dialog */}
          <CustomDialogSmall
            open={openChart}
            onClose={() => setOpenChart(false)}
            title={`${marker.short} - Trend`}
            content={<>
             
            <Grid size={12} sx={{pt:2, pr:5}}>
                <Typography variant="h3" color={lineColor} textAlign="right" fontWeight={500}>
                    {latestValue}{' '}
                    <Typography component="span" variant="subtitle2" fontWeight={500}>{marker.unit}</Typography>
                </Typography>
                <Typography variant="body1" textAlign="right" sx={{ color: theme.palette.mode === "light" ? theme.palette.common.black : theme.palette.common.white }}>
                    {latestDate}
                </Typography>
            </Grid>
            
             <Grid size={12} ref={containerRef}>
              {history.length > 1 && (
                <BloodLineChart
                  data={chartData}
                  unit={marker.unit}
                  xLabels={xLabels}
                  latestPoint={latestPoint}
                  latestColor={statusColors[marker.status]}  // green/orange/red from backend
                  lineColor={lineColor}
                  lowerReference={parseFloat(marker.reference.lower_green)}
                  upperReference={parseFloat(marker.reference.upper_green)}
                  width={width}
                  height={240}
                />
              )}
              </Grid>
    
             </>}
            />

            {/* History Dialog */}
            <CustomDialogSmall
              open={openHistory}
              onClose={() => setOpenHistory(false)}
              title={`${marker.name} - History`}
              content={
              <Box sx={{ mt: 1 }}>
                {(!marker.history || marker.history.length === 0) ? (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    No historical data available.
                  </Typography>
                ) : (
                  <Table
                      sx={{
                        minWidth: 500,
                        "& .MuiTableCell-root": {
                          py: 1, // row height ↑
                          fontSize: "0.75rem",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Value
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Unit
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {marker.history
                          .slice()
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map((row) => (
                            <TableRow
                              key={row.id}
                              sx={{
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                              }}
                            >
                              <TableCell>
                                {new Date(row.date).toLocaleDateString()}
                              </TableCell>

                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {parseFloat(row.value).toFixed(2)}
                              </TableCell>

                              <TableCell align="right">{row.unit}</TableCell>

                              <TableCell align="right">
                                <Chip
                                  label={row.status}
                                  size="medium" // 👈 bigger chip
                                  sx={{
                                    bgcolor: theme.palette.traffic[row.status],
                                    color: "white",
                                    fontWeight: 600,
                                    px: 1,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                )}
              </Box>
            }
            />

             {/* Add Data Form - Dialog */}
                <CustomDialogSmall
                  open={openAdd}
                  onClose={() => setOpenAdd(false)}
                  title="Add Bloodmarker Measure"
                  content={
                    <BloodMarkerAddForm
                        url_add={`${API_URL}/measurements/${userId}/${marker.short}/`}
                        targetNavigation="/BloodAllWelcome"              
                        fieldName="value"
                        fieldLabel={marker.name}
                        placeholder={marker.name}
                  />}
                />


  </> 
  );
};

export default MarkerCardComp;
